/* eslint-disable */
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useAppData } from '../../context/AppDataContext';
import {
  propertiesAPI, paymentsAPI, expensesAPI, companyExpensesAPI, companiesAPI,
  prospectsAPI, projectsAPI, calendarAPI, settingsAPI, adminAPI
} from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { formatMoney, exportSummaryByCurrency, exportSummaryLines } from '../../utils/currencyTotals';

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmt = (v) => formatCurrency(v || 0);
// BUG-005c: a figure for one property is shown with that property's own currency symbol
const fmtIn = (currency, v) => formatMoney(v, currency);
const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-GB') : '';
const safe = (v) => (v === null || v === undefined) ? '' : String(v);
const esc = (v) => {
  const s = safe(v);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
  return s;
};
const toCSV = (rows, headers) => {
  const lines = [headers.join(',')];
  rows.forEach(r => lines.push(headers.map(h => esc(r[h])).join(',')));
  return lines.join('\n');
};
const downloadFile = (content, filename, mime = 'text/plain;charset=utf-8') => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ── Main Component ─────────────────────────────────────────────────────────────

export default function DataExport() {
  const { user, isOwner } = useAuth();
  const { settings } = useAppData();
  const [progress, setProgress] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  if (!isOwner()) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
        <h3>Owner Access Only</h3>
        <p style={{ color: 'var(--text-secondary)' }}>Only organisation owners can export all data.</p>
      </div>
    );
  }

  const handleExport = async () => {
    setLoading(true); setDone(false); setError('');
    const orgName = settings?.company_name || user?.organizationName || 'HMO';
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const files = {};

    try {
      // ── 1. Properties ────────────────────────────────────────────────────────
      setProgress('Fetching properties…');
      const properties = await propertiesAPI.getAll();
      // COMPANY-009: a data export that omits which company owns what is not
      // a complete record — it's the field an accountant needs first to split
      // the figures between entities. Fetched once and reused for both the
      // Properties and Company Expenses sheets.
      const companiesList = await companiesAPI.getAll().catch(() => []);
      const companyNameById = {};
      (companiesList || []).forEach(c => { companyNameById[String(c.id)] = c.name; });
      const companyNameFor = (id, ownership) => {
        // Guarded on ownership: a personally- or jointly-held property must
        // never report a company, even if a legacy row still carries a stale
        // company_id from before the COMPANY-003 rule was enforced.
        if (ownership !== undefined && String(ownership||'').toLowerCase() !== 'company') return '';
        return id ? (companyNameById[String(id)] || '') : '';
      };
      const propRows = properties.map(p => {
        const cd = p.capitalData || p.capital_data || {};
        return {
          'ID': p.id, 'Address': p.address, 'Type': p.propertyType,
          'Ownership': p.ownership,
          // FEAT-002: the share only means something for a jointly owned property; blank means not set
          'Ownership share %': String(p.ownership||'').toLowerCase() === 'joint' && p.ownershipSharePercent != null ? p.ownershipSharePercent : '',
          'Company': companyNameFor(p.companyId || p.company_id, p.ownership),
          'Currency': p.currency,
          'Rooms': (p.rooms||[]).length,
          'Occupied Rooms': (p.rooms||[]).filter(r=>r.status==='occupied').length,
          'Purchase Price': cd.purchasePrice||'', 'Current Value': cd.currentValue||'',
          'Purchase Date': fmtDate(cd.purchaseDate),
          'Mortgage Provider': cd.mortgageProvider||'',
          'Mortgage Balance': cd.mortgageBalance||'',
          'Mortgage Rate %': cd.mortgageRate||'',
          'Repayment Type': cd.repaymentType||'',
          'Rate Expiry Date': fmtDate(cd.rateExpiryDate),
          'Additional Details': p.additionalDetails || p.additional_details || ''
        };
      });
      files['01_Properties.csv'] = toCSV(propRows, Object.keys(propRows[0] || {}));

      // ── 2. Rooms / Tenants ───────────────────────────────────────────────────
      setProgress('Fetching rooms & tenants…');
      const roomRows = [];
      properties.forEach(p => {
        (p.rooms||[]).forEach(r => roomRows.push({
          'Property': p.address, 'Room Name': r.roomName,
          'Status': r.status, 'Tenant Name': r.tenantName||'',
          'Monthly Rent': r.rentAmount||0,
          'Payment Due Day': r.paymentDueDay||1,
          'Lease Start': fmtDate(r.leaseStart), 'Lease End': fmtDate(r.leaseEnd),
          'Vacant Since': fmtDate(r.vacantSince)
        }));
      });
      files['02_Rooms_Tenants.csv'] = toCSV(roomRows, Object.keys(roomRows[0] || {}));

      // ── 3. Licences ──────────────────────────────────────────────────────────
      setProgress('Fetching licences…');
      const licRows = [];
      properties.forEach(p => {
        (p.licences||[]).filter(l=>!l.archived).forEach(l => licRows.push({
          'Property': p.address, 'Type': l.type, 'Description': l.description||'',
          'Reference Number': l.referenceNumber||'',
          'Valid From': fmtDate(l.validFrom), 'Valid To': fmtDate(l.validTo),
          'Status': l.validTo && new Date(l.validTo) < new Date() ? 'Expired' : 'Valid',
          'Notes': l.notes||''
        }));
      });
      files['03_Licences.csv'] = toCSV(licRows, Object.keys(licRows[0] || {}));

      // ── 4. Payments ──────────────────────────────────────────────────────────
      setProgress('Fetching payments…');
      const payments = await paymentsAPI.getAll();
      const propMap = {}; properties.forEach(p => { propMap[p.id] = p; });
      const payRows = payments.map(p => {
        const prop = propMap[p.propertyId || p.property_id];
        const room = (prop?.rooms||[]).find(r => String(r.id) === String(p.roomId || p.room_id));
        return {
          'Date Received': fmtDate(p.paidDate || p.paid_date),
          'Month': p.month, 'Property': prop?.address||'',
          'Room': room?.roomName||'', 'Tenant': room?.tenantName||'',
          'Amount': p.amount
        };
      }).sort((a,b) => b['Date Received'].localeCompare(a['Date Received']));
      files['04_Payments.csv'] = toCSV(payRows, Object.keys(payRows[0] || {}));

      // ── 5. Property Expenses ─────────────────────────────────────────────────
      setProgress('Fetching property expenses…');
      const expenses = await expensesAPI.getAll();
      const expRows = expenses.map(e => {
        const prop = propMap[e.propertyId || e.property_id];
        return {
          'Date': fmtDate(e.date), 'Property': prop?.address||'',
          'Category': e.category, 'Description': e.description||'',
          'Amount': e.amount,
          'Paid': (e.paid === true || e.paid === 'true') ? 'Yes' : 'No'
        };
      }).sort((a,b) => b.Date.localeCompare(a.Date));
      files['05_Property_Expenses.csv'] = toCSV(expRows, Object.keys(expRows[0] || {}));

      // ── 6. Company Expenses ──────────────────────────────────────────────────
      setProgress('Fetching company expenses…');
      const compExp = await companyExpensesAPI.getAll();
      const compRows = compExp.map(e => ({
        'Date': fmtDate(e.date),
        'Company': companyNameFor(e.company_id || e.companyId) || 'Unassigned',
        'Category': e.category,
        'Description': e.description||'', 'Amount': e.amount,
        'Paid': (e.paid === true || e.paid === 'true') ? 'Yes' : 'No',
        'Receipt': e.receipt ? 'Yes' : 'No'
      })).sort((a,b) => b.Date.localeCompare(a.Date));
      files['06_Company_Expenses.csv'] = toCSV(compRows, Object.keys(compRows[0] || {}));

      // ── 7. Prospective Properties ────────────────────────────────────────────
      setProgress('Fetching prospective properties…');
      const prospects = await prospectsAPI.getAll();
      const prospRows = prospects.map(p => {
        const rb = (typeof p.refurb_breakdown === 'string' ? JSON.parse(p.refurb_breakdown||'{}') : p.refurb_breakdown)||{};
        return {
          'Address': p.address, 'Status': p.status,
          'Asking Price': p.asking_price||p.askingPrice||'',
          'Sale Price': p.sale_price||p.salePrice||'',
          'Bedrooms': rb._beds||p.bedrooms||'',
          'Expected Rent': p.expected_rent||p.expectedRent||'',
          'Refurb Costs': p.refurb_costs||p.refurbCosts||'',
          'Purchase Costs': p.purchase_costs||p.purchaseCosts||'',
          'Finished Value': p.finished_value||p.finishedValue||'',
          'Management (mo)': rb._mgmt||0, 'Utilities (mo)': rb._utils||0,
          'Mortgage (mo)': rb._mort||0, 'Repairs (mo)': rb._rep||0,
          'Mortgage Rate %': p.mortgage_rate||p.mortgageRate||'',
          'Property Link': rb._link||p.propertyLink||'',
          'Archived': p.archived ? 'Yes' : 'No',
          'Notes': p.notes||''
        };
      });
      files['07_Prospective_Properties.csv'] = toCSV(prospRows, Object.keys(prospRows[0] || {}));

      // ── 8. Projects ──────────────────────────────────────────────────────────
      setProgress('Fetching projects…');
      const projects = await projectsAPI.getAll();
      const projRows = projects.map(p => ({
        'Name': p.name, 'Status': p.status,
        'Property': propMap[p.property_id||p.propertyId]?.address||'General',
        'Budget': p.budget||'',
        'Start Date': fmtDate(p.start_date||p.startDate),
        'End Date': fmtDate(p.end_date||p.endDate),
        'Total Tasks': (p.tasks||[]).length,
        'Completed Tasks': (p.tasks||[]).filter(t=>t.completed).length,
        'Description': p.description||''
      }));
      files['08_Projects.csv'] = toCSV(projRows, Object.keys(projRows[0] || {}));

      // ── 9. Project Tasks ─────────────────────────────────────────────────────
      setProgress('Fetching project tasks…');
      const taskRows = [];
      projects.forEach(p => {
        (p.tasks||[]).forEach(t => taskRows.push({
          'Project': p.name, 'Task': t.name,
          'Assigned To': t.assignedTo||'',
          'Status': t.completed ? 'Completed' : 'Pending',
          'Due Date': fmtDate(t.dueDate), 'Duration (days)': t.duration||'',
          'Description': t.description||''
        }));
      });
      files['09_Project_Tasks.csv'] = toCSV(taskRows, Object.keys(taskRows[0] || {}));

      // ── 10. Calendar Tasks ───────────────────────────────────────────────────
      setProgress('Fetching calendar tasks…');
      const calTasks = await calendarAPI.getAll();
      const calRows = calTasks.map(t => ({
        'Date': fmtDate(t.date), 'Title': t.title, 'Type': t.type||'manual',
        'Description': t.description||'',
        'Completed': t.completed ? 'Yes' : 'No',
        'Property': propMap[t.property_id||t.propertyId]?.address||''
      })).sort((a,b) => a.Date.localeCompare(b.Date));
      files['10_Calendar_Tasks.csv'] = toCSV(calRows, Object.keys(calRows[0] || {}));

      // ── 11. Settings ─────────────────────────────────────────────────────────
      setProgress('Fetching settings…');
      const settingsData = await settingsAPI.get();
      const settingsRows = Object.entries(settingsData)
        .filter(([k]) => !k.startsWith('org_logo')) // exclude base64 logo
        .map(([k, v]) => ({ 'Key': k, 'Value': String(v||'') }));
      files['11_Settings.csv'] = toCSV(settingsRows, ['Key', 'Value']);

      // ── 12. Audit Logs ───────────────────────────────────────────────────────
      setProgress('Fetching audit logs…');
      try {
        const auditLogs = await adminAPI.getAuditLogs();
        const auditRows = auditLogs.map(l => ({
          'Date/Time': fmtDate(l.created_at), 'Username': l.username,
          'Action': l.event_action, 'Type': l.event_type||'',
          'Details': JSON.stringify(l.event_details||{}),
          'IP Address': l.ip_address||''
        }));
        files['12_Audit_Logs.csv'] = toCSV(auditRows, Object.keys(auditRows[0] || {}));
      } catch (e) { /* non-critical */ }

      // ── 13. Summary JSON (full data snapshot) ────────────────────────────────
      setProgress('Building full JSON snapshot…');
      const snapshot = {
        exportedAt: new Date().toISOString(),
        exportedBy: user?.username,
        organisation: orgName,
        summary: {
          properties: properties.length,
          rooms: properties.reduce((s,p)=>s+(p.rooms||[]).length,0),
          payments: payments.length,
          propertyExpenses: expenses.length,
          companyExpenses: compExp.length,
          prospects: prospects.length,
          projects: projects.length,
          calendarTasks: calTasks.length,
        },
        data: {
          properties: properties.map(p => ({
            ...p,
            // Strip base64 docs to keep file size manageable
            refurbBreakdown: (() => {
              const rb = p.refurbBreakdown || {};
              const { _docs, ...rest } = rb;
              return rest;
            })()
          })),
          payments, expenses, companyExpenses: compExp,
          prospects: prospects.map(p => {
            const rb = (typeof p.refurb_breakdown === 'string' ? JSON.parse(p.refurb_breakdown||'{}') : p.refurb_breakdown)||{};
            const { _docs, ...restRb } = rb;
            return { ...p, refurb_breakdown: restRb };
          }),
          projects, calendarTasks: calTasks, settings: settingsData
        }
      };
      files['00_Full_Snapshot.json'] = JSON.stringify(snapshot, null, 2);

      // ── 14. Summary Report (human-readable) ──────────────────────────────────
      setProgress('Building summary report…');
      // BUG-005c: totals are worked out one currency at a time (euro and pound amounts are never added together)
      const summaryGroups = exportSummaryByCurrency({ properties, payments, expenses });

      const summary = [
        `DATA EXPORT SUMMARY`,
        `Organisation: ${orgName}`,
        `Exported By: ${user?.username}`,
        `Export Date: ${new Date().toLocaleDateString('en-GB', {day:'numeric',month:'long',year:'numeric'})}`,
        ``,
        `═══════════════════════════════════════`,
        `PORTFOLIO OVERVIEW`,
        `═══════════════════════════════════════`,
        `Total Properties:        ${properties.length}`,
        `Total Rooms:             ${properties.reduce((s,p)=>s+(p.rooms||[]).length,0)}`,
        `Occupied Rooms:          ${properties.reduce((s,p)=>s+(p.rooms||[]).filter(r=>r.status==='occupied').length,0)}`,
        `Vacant Rooms:            ${properties.reduce((s,p)=>s+(p.rooms||[]).filter(r=>r.status==='vacant').length,0)}`,
        ...exportSummaryLines(summaryGroups, 'portfolio'),
        ``,
        `═══════════════════════════════════════`,
        `FINANCIALS (Last 12 Months)`,
        `═══════════════════════════════════════`,
        ...exportSummaryLines(summaryGroups, 'financial'),
        ``,
        `═══════════════════════════════════════`,
        `DATA COUNTS`,
        `═══════════════════════════════════════`,
        `Payment Records:         ${payments.length}`,
        `Property Expense Records:${expenses.length}`,
        `Company Expense Records: ${compExp.length}`,
        `Prospective Properties:  ${prospects.length}`,
        `Projects:                ${projects.length}`,
        `Calendar Tasks:          ${calTasks.length}`,
        ``,
        `═══════════════════════════════════════`,
        `PROPERTIES DETAIL`,
        `═══════════════════════════════════════`,
        ...properties.map(p => {
          const cd = p.capitalData||p.capital_data||{};
          const occ = (p.rooms||[]).filter(r=>r.status==='occupied').length;
          const tot = (p.rooms||[]).length;
          const rent = (p.rooms||[]).filter(r=>r.status==='occupied').reduce((s,r)=>s+parseFloat(r.rentAmount||0),0);
          return [
            ``,
            `  ${p.address}`,
            `  Type: ${p.propertyType||''}  |  Ownership: ${p.ownership||''}${companyNameFor(p.companyId||p.company_id, p.ownership) ? '  |  Company: ' + companyNameFor(p.companyId||p.company_id, p.ownership) : ''}  |  Occupancy: ${occ}/${tot}`,
            `  Monthly Rent: ${fmtIn(p.currency, rent)}  |  Annual: ${fmtIn(p.currency, rent*12)}`,
            cd.purchasePrice ? `  Purchase Price: ${fmtIn(p.currency, cd.purchasePrice)}  |  Current Value: ${fmtIn(p.currency, cd.currentValue)}  |  Equity: ${fmtIn(p.currency, parseFloat(cd.currentValue||0)-parseFloat(cd.mortgageBalance||0))}` : '',
            cd.mortgageProvider ? `  Mortgage: ${cd.mortgageProvider} @ ${cd.mortgageRate||0}%  |  Balance: ${fmtIn(p.currency, cd.mortgageBalance)}  |  Expiry: ${fmtDate(cd.rateExpiryDate)}` : '',
            `  Licences: ${(p.licences||[]).filter(l=>!l.archived).length}`,
          ].filter(Boolean).join('\n');
        }),
        ``,
        `═══════════════════════════════════════`,
        `FILES IN THIS EXPORT`,
        `═══════════════════════════════════════`,
        `00_Full_Snapshot.json     - Complete machine-readable data backup`,
        `01_Properties.csv         - All properties with capital data`,
        `02_Rooms_Tenants.csv      - All rooms and tenant details`,
        `03_Licences.csv           - All property licences and certificates`,
        `04_Payments.csv           - All rent payment records`,
        `05_Property_Expenses.csv  - All property-level expenses`,
        `06_Company_Expenses.csv   - All company-level expenses`,
        `07_Prospective_Properties.csv - Pipeline and analysis data`,
        `08_Projects.csv           - All projects`,
        `09_Project_Tasks.csv      - All project tasks`,
        `10_Calendar_Tasks.csv     - All calendar tasks and reminders`,
        `11_Settings.csv           - Organisation settings`,
        `12_Audit_Logs.csv         - System audit trail`,
        `13_Export_Summary.txt     - This file`,
      ].join('\n');
      files['13_Export_Summary.txt'] = summary;

      // ── Download all files ───────────────────────────────────────────────────
      setProgress('Downloading files…');
      const delay = (ms) => new Promise(r => setTimeout(r, ms));

      const fileList = Object.entries(files);
      for (let i = 0; i < fileList.length; i++) {
        const [name, content] = fileList[i];
        setProgress(`Downloading ${i+1}/${fileList.length}: ${name}`);
        const mime = name.endsWith('.json') ? 'application/json' : name.endsWith('.csv') ? 'text/csv;charset=utf-8' : 'text/plain;charset=utf-8';
        const safeName = `${orgName.replace(/[^a-z0-9]/gi,'_')}_${stamp}_${name}`;
        downloadFile('\uFEFF' + content, safeName, mime); // BOM for Excel UTF-8
        await delay(400); // stagger downloads so browser doesn't block them
      }

      setDone(true);
      setProgress('');
    } catch (err) {
      console.error('Export error:', err);
      setError(err.error || err.message || 'Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '1.5rem', borderTop: '4px solid var(--brand-green)' }}>
        <div className="card-header">
          <span className="card-title">📦 Export All Organisation Data</span>
        </div>

        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem', fontSize: '0.875rem', color: '#15803d' }}>
          <strong>What's included in the export:</strong>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem 2rem', marginTop: '0.5rem' }}>
            {[
              '✅ Properties & capital data', '✅ Rooms & tenant details',
              '✅ Licences & certificates', '✅ All rent payment records',
              '✅ Property expenses', '✅ Company expenses',
              '✅ Prospective properties & ROI', '✅ Projects & tasks',
              '✅ Calendar tasks & reminders', '✅ Organisation settings',
              '✅ Audit trail', '✅ Full JSON data snapshot',
            ].map(item => <span key={item}>{item}</span>)}
          </div>
        </div>

        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem', fontSize: '0.875rem', color: '#1e40af' }}>
          <strong>ℹ️ How it works:</strong> Clicking Export will download <strong>14 files</strong> to your Downloads folder — 12 CSV files (openable in Excel/Google Sheets), 1 JSON file (complete data backup for re-import), and 1 summary text report. Files are staggered slightly so your browser accepts all of them.
        </div>

        {error && (
          <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>❌ {error}</div>
        )}

        {loading && (
          <div style={{ background: '#fefce8', border: '1px solid #fef08a', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: '20px', height: '20px', border: '3px solid #f59e0b', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
            <span style={{ color: '#92400e', fontWeight: 600 }}>{progress}</span>
          </div>
        )}

        {done && !loading && (
          <div style={{ background: '#dcfce7', border: '1px solid #16a34a', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem' }}>
            <strong style={{ color: '#15803d' }}>✅ Export complete!</strong>
            <p style={{ color: '#15803d', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
              14 files have been downloaded to your Downloads folder. Check your browser's download bar if they don't appear immediately.
            </p>
          </div>
        )}

        <button
          className="btn btn-primary"
          onClick={handleExport}
          disabled={loading}
          style={{ fontSize: '1rem', padding: '0.75rem 2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          {loading ? '⟳ Exporting…' : '📦 Export All Data'}
        </button>
      </div>

      <div className="card" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
        <div className="card-header"><span className="card-title" style={{ color: '#dc2626' }}>⚠️ Stopping the Service</span></div>
        <p style={{ fontSize: '0.875rem', color: '#7f1d1d', marginBottom: '0.75rem' }}>
          If you are cancelling your subscription, please make sure you have:
        </p>
        <ol style={{ fontSize: '0.875rem', color: '#7f1d1d', paddingLeft: '1.25rem', margin: 0, lineHeight: '2' }}>
          <li>Exported all your data using the button above</li>
          <li>Verified all files have downloaded correctly and can be opened</li>
          <li>Saved the <strong>00_Full_Snapshot.json</strong> file securely — this is your complete data backup</li>
          <li>Notified any team members in your organisation</li>
        </ol>
        <p style={{ fontSize: '0.875rem', color: '#7f1d1d', marginTop: '0.75rem', fontStyle: 'italic' }}>
          All data is permanently deleted when a subscription ends. This export is your only copy.
        </p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
